import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listGolonganDarah,
  listHubunganKeluarga,
  listLokasiKuliah,
} from "@/constants";
import { useDebounce } from "@/hooks/useDebounce";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import { formatDate } from "@/lib/utils";
import {
  getBiodata,
  getListAgama,
  getListNegara,
  getListPerguruanTinggi,
  getListPerguruanTinggiProdi,
} from "@/services/getData";
import {
  Agama,
  BiodataSateStudentExchange,
  DataPendaftaran,
  DataSiswa,
  GolonganDarahEnum,
  HubunganKeluargaEnum,
  LokasiKuliah,
  Negara,
  Option,
  PerguruanTinggi,
  PerguruanTinggiProdi,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { FaCamera } from "react-icons/fa6";
import PhoneInputWithCountrySelect from "react-phone-number-input";

// Component SectionCard didefinisikan di luar komponen utama
// untuk mencegah re-render yang menyebabkan input kehilangan fokus
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-background border rounded-xl p-6 mb-6 shadow-sm">
    <h2 className="text-lg font-semibold border-b pb-3 mb-5 text-foreground/80">
      {title}
    </h2>
    <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-5 gap-x-6">
      {children}
    </div>
  </div>
);

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
}: {
  form: BiodataSateStudentExchange;
  setForm: React.Dispatch<SetStateAction<BiodataSateStudentExchange>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const { dataPeriode } = useTimePeriode();
  const optionsProgram = dataPeriode?.sys_daftar_program?.map((item) => {
    return {
      label: item.ref_daftar_program.nama_program,
      value: item.id_ref_daftar_program,
    };
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openNegara, setOpenNegara] = useState(false);
  const [openListProgram, setOpenListProgram] = useState(false);

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const [openAsalProdi, setOpenAsalProdi] = useState(false);
  const [openAsalUniversitas, setOpenAsalUniversitas] = useState(false);
  const [searchQueryUniversitas, setSearchQueryUniversitas] = useState("");
  const debouncedQueryUniversitas = useDebounce(searchQueryUniversitas, 1000);

  const { data: optionsPt, isPending: isPendingOptionsPt } = useQuery({
    queryKey: ["listPerguruanTinggi", debouncedQueryUniversitas, form.asal_pt],
    queryFn: () =>
      getListPerguruanTinggi(
        "1",
        "10",
        debouncedQueryUniversitas
          ? debouncedQueryUniversitas
          : (form.asal_pt as string),
      ),
    select: (data) => {
      if (!data) return [];
      return data?.map((item: PerguruanTinggi) => ({
        label: item.nama_perguruan_tinggi,
        value: `${item.id_feeder}`,
        list_prodi: item.pt_prodi,
      }));
    },
  });

  const { data: dataAsalProdi } = useQuery({
    queryKey: ["listAsalProdi", form.asal_pt],
    queryFn: () => getListPerguruanTinggiProdi("1", "1000", form.asal_pt || ""),
    select: (data) => {
      return data?.map((item: PerguruanTinggiProdi) => ({
        label: `${item.nama_program_studi} (${item.nama_jenjang_pendidikan})`,
        value: `${item.id_feeder}`,
        nama_jenjang_pendidikan: item.nama_jenjang_pendidikan,
      }));
    },
    enabled: !!form.asal_pt,
  });

  const optionsAsalProdi = dataAsalProdi ? [...dataAsalProdi] : [];

  const isNApt = form.asal_pt == "4b4b23c1-8e0c-4825-89fa-765401c5e9c5";
  // const isNAprodi = form.asal_prodi == "0";

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;

    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      id_ref_daftar_program: dataPendaftaran?.id_ref_daftar_program || "",
      ...dataSiswa,
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      asal_sekolah: dataPendaftaran?.asal_sekolah,
      asal_jurusan_sekolah_internasional:
        dataPendaftaran?.asal_jurusan_sekolah_internasional,
      asal_pt: dataPendaftaran?.asal_pt,
      asal_pt_na: dataPendaftaran?.asal_pt_na,
      asal_prodi: dataPendaftaran?.asal_prodi,
      asal_prodi_na: dataPendaftaran?.asal_prodi_na,
      tahun_ijazah_terakhir: dataPendaftaran?.tahun_ijazah_terakhir,
      nilai_IPK: dataPendaftaran?.nilai_IPK,
      // Boolean conversions
      is_bekerja: Number(dataPendaftaran?.is_bekerja) === 1,
      is_alergi_obat: Number(dataPendaftaran?.is_alergi_obat) === 1,
      tempat_bekerja: dataPendaftaran?.tempat_bekerja,
      penghasilan_bekerja: dataPendaftaran?.penghasilan_bekerja,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="mb-6 font-bold text-2xl tracking-tight">
        Personal Information
      </h1>

      {isPendingUser || isPendingDataSiswa ? (
        <div className="space-y-4">
          <SectionCard title="Loading Data...">
            {Array(8)
              .fill(null)
              .map((_, index) => (
                <SkeletonRow key={index} />
              ))}
          </SectionCard>
        </div>
      ) : (
        <>
          {/* PHOTO UPLOAD SECTION */}
          <div className="bg-background border rounded-xl p-6 mb-6 shadow-sm flex flex-col md:flex-row gap-8 items-center md:items-start">
            <div className="flex-shrink-0">
              <div className="flex flex-col items-center">
                <div
                  className="group relative w-[12rem] aspect-[3/4] rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-secondary/50 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-center items-center"
                  onClick={() => uploadRef.current?.click()}
                >
                  {preview || form.path_foto ? (
                    <>
                      <img
                        src={preview || form.path_foto || ""}
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-medium">
                        Change Photo
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-muted-foreground group-hover:text-primary transition-colors">
                      <div className="bg-secondary p-4 rounded-full mb-3">
                        <FaCamera size={24} />
                      </div>
                      <p className="font-semibold text-sm">Upload Photo</p>
                      <p className="text-xs text-center px-4 mt-1 opacity-70">
                        Recommended 3:4 aspect ratio
                      </p>
                    </div>
                  )}
                </div>
                <input
                  ref={uploadRef}
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif"
                  className="hidden"
                  onChange={(event) => {
                    const files = event.target.files;
                    if (files && files[0]) setFile(files[0]);
                  }}
                />
                <div className="mt-3 text-sm text-muted-foreground font-medium text-center">
                  {file ? file.name : "Formal Photo"}
                </div>
                <InputError
                  text={error?.response?.data?.errors?.foto?.join(",")}
                />
              </div>
            </div>

            {/* IDENTITY SECTION */}
            <div className="flex-grow w-full grid grid-cols-1 gap-4">
              <h3 className="font-semibold text-lg text-foreground/80 mb-2">
                Identity Details
              </h3>

              <div>
                <Label className="dark:text-slate-200">Program</Label>
                <Combobox
                  open={openListProgram}
                  setOpen={setOpenListProgram}
                  value={form.id_ref_daftar_program}
                  setValue={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      id_ref_daftar_program: value,
                    }));
                  }}
                  options={optionsProgram || []}
                  placeholder={`Select Program`}
                />
              </div>

              {isKelasDenpasar && (
                <div>
                  <Label>Lecture Location (Campus)</Label>
                  <Select
                    value={form.pilihan_lokasi_kuliah || ""}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        pilihan_lokasi_kuliah: value as LokasiKuliah,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Campus Location">
                        {form.pilihan_lokasi_kuliah &&
                          listLokasiKuliah?.find(
                            (value) => value === form.pilihan_lokasi_kuliah,
                          )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {listLokasiKuliah?.map((value, index) => (
                        <SelectItem
                          key={index}
                          value={`${value.toLowerCase()}`}
                        >
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.pilihan_lokasi_kuliah?.join(
                      ",",
                    )}
                  />
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label required>Full Name (Without Title)</Label>
                  <Input
                    required
                    value={form.nama_lengkap || ""}
                    placeholder="Enter full name as per diploma..."
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_lengkap: e.target.value,
                      }))
                    }
                  />
                  <p className="text-muted-foreground text-xs mt-1">
                    Full name must match your latest diploma exactly, without
                    any academic titles!
                  </p>
                  <InputError
                    text={error?.response?.data?.errors?.nama_lengkap?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>First Name</Label>
                  <Input
                    required
                    value={form.nama_depan || ""}
                    placeholder="Enter First Name..."
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_depan: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_depan?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Last Name</Label>
                  <Input
                    required
                    value={form.nama_belakang || ""}
                    placeholder="Enter Last Name..."
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_belakang: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_belakang?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Mother’s Name</Label>
                  <Input
                    required
                    value={form.nama_ibu_kandung || ""}
                    placeholder="Enter Mother’s Name..."
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_ibu_kandung: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_ibu_kandung?.join(
                      ",",
                    )}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label required>Passport Number</Label>
                  <Input readOnly disabled value={dataSiswa?.nik || ""} />
                </div>
                <div>
                  <Label required>Email</Label>
                  <Input readOnly disabled value={dataSiswa?.email || ""} />
                </div>
              </div>
            </div>
          </div>

          {/* PERSONAL DETAILS SECTION */}
          <SectionCard title="Personal Details">
            {/* <div>
              <Label>Visa Number</Label>
              <Input
                value={form.nomor_viza || ""}
                placeholder="Enter Visa Number..."
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nomor_viza: e.target.value,
                  }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.nomor_viza?.join(",")}
              />
            </div> */}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label required>Date of Birth</Label>
                <Input
                  required
                  type="date"
                  value={form.tanggal_lahir || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tanggal_lahir: e.target.value,
                    }))
                  }
                />
                <p className="text-primary text-xs mt-1 font-medium">
                  {form.tanggal_lahir &&
                    formatDate(form.tanggal_lahir, "medium", "en-US")}
                </p>
                <InputError
                  text={error?.response?.data?.errors?.tanggal_lahir?.join(",")}
                />
              </div>
              <div>
                <Label required>Place of Birth</Label>
                <Input
                  required
                  value={form.tempat_lahir || ""}
                  placeholder="Enter Place of Birth..."
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tempat_lahir: e.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tempat_lahir?.join(",")}
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <Label required>Gender</Label>
                <Select
                  value={form.jenis_kelamin || ""}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      jenis_kelamin: value as "L" | "P",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Gender">
                      {form.jenis_kelamin &&
                        [
                          { label: "Male", value: "L" },
                          { label: "Female", value: "P" },
                        ]?.find((option) => option.value === form.jenis_kelamin)
                          ?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Male</SelectItem>
                    <SelectItem value="P">Female</SelectItem>
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.jenis_kelamin?.join(",")}
                />
              </div>

              <div>
                <Label required>Blood Type</Label>
                <Select
                  value={form.golongan_darah || ""}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      golongan_darah: value as GolonganDarahEnum,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Blood Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {listGolonganDarah?.map((option: Option, index: number) => (
                      <SelectItem key={index} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.golongan_darah?.join(
                    ",",
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label required>Height</Label>
                <Input
                  required
                  value={form.tinggi_badan || ""}
                  placeholder="Enter your height in cm..."
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tinggi_badan: Number(e.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tinggi_badan?.join(",")}
                />
              </div>
              <div>
                <Label required>Weight</Label>
                <Input
                  required
                  value={form.berat_badan || ""}
                  placeholder="Enter your wight in kg..."
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      berat_badan: Number(e.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.berat_badan?.join(",")}
                />
              </div>
            </div>

            {/* NEW INPUT: DRUG ALLERGY */}
            <div className="lg:col-span-2">
              <Label>Do you have any drug allergies?</Label>
              <div className="mt-3 flex gap-6">
                <div
                  className={`flex items-center gap-3 border p-4 rounded-xl cursor-pointer transition-colors w-full ${
                    form.is_alergi_obat === false
                      ? "bg-secondary border-primary"
                      : "hover:bg-accent"
                  }`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, is_alergi_obat: false }))
                  }
                >
                  <Checkbox checked={form.is_alergi_obat === false} />
                  <Label className="cursor-pointer">No</Label>
                </div>

                <div
                  className={`flex items-center gap-3 border p-4 rounded-xl cursor-pointer transition-colors w-full ${
                    form.is_alergi_obat === true
                      ? "bg-secondary border-primary"
                      : "hover:bg-accent"
                  }`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, is_alergi_obat: true }))
                  }
                >
                  <Checkbox checked={form.is_alergi_obat === true} />
                  <Label className="cursor-pointer">Yes</Label>
                </div>
              </div>
              <InputError
                text={error?.response?.data?.errors?.is_alergi_obat?.join(",")}
              />
            </div>

            <div>
              <Label required>Religion</Label>
              <Select
                value={form.id_ref_agama || ""}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, id_ref_agama: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Religion">
                    {form.id_ref_agama &&
                      dataAgama?.find(
                        (agama: Agama) =>
                          agama.id_ref_agama == String(form.id_ref_agama),
                      )?.nama_agama_en}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {dataAgama?.map((agama: Agama) => (
                    <SelectItem
                      key={agama.id_ref_agama}
                      value={`${agama.id_ref_agama}`}
                    >
                      {agama.nama_agama_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InputError
                text={error?.response?.data?.errors?.id_ref_agama?.join(",")}
              />
            </div>
          </SectionCard>

          {/* CONTACT & ADDRESS */}
          <SectionCard title="Contact & Address">
            <div>
              <Label>Phone Number (WhatsApp)</Label>
              <PhoneInputWithCountrySelect
                placeholder="Enter phone number"
                value={form.handphone || ""}
                onChange={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    handphone: newValue || "",
                  }))
                }
                className="bg-secondary rounded-full py-2 px-3 border border-input"
              />
              <InputError
                text={error?.response?.data?.errors?.handphone?.join(",")}
              />
            </div>

            <div className="lg:col-span-2">
              <Label required>Origin Address</Label>
              <Input
                required
                placeholder="Origin Address (including building, block and aprtment number)"
                value={form.jalan || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, jalan: e.target.value }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.jalan?.join(",")}
              />
            </div>

            <div>
              <Label required>City / Town</Label>
              <Input
                placeholder="Enter City/Town..."
                value={form.kelurahan || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    kelurahan: e.target.value,
                  }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.kelurahan?.join(",")}
              />
            </div>

            <div>
              <div>
                <Label required>State / Province / Region</Label>
                <Input
                  placeholder="Enter State/Province..."
                  value={form.provinsi_state || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      provinsi_state: e.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.provinsi_state?.join(
                    ",",
                  )}
                />
              </div>
            </div>

            <div>
              <Label required>Country</Label>
              <Combobox
                open={openNegara}
                setOpen={setOpenNegara}
                value={form.id_ref_negara || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({ ...prev, id_ref_negara: newValue }))
                }
                options={dataNegara}
                placeholder="Select Country"
              />
              <InputError
                text={error?.response?.data?.errors?.id_ref_negara?.join(",")}
              />
            </div>

            <div>
              <Label required>Postal Code</Label>
              <Input
                placeholder="Enter Postal Code..."
                value={form.kode_pos || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, kode_pos: e.target.value }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.kode_pos?.join(",")}
              />
            </div>

            <div className="lg:col-span-2">
              <Label required>Address in Indonesia:</Label>
              <Input
                required
                placeholder="Address in Indonesia:..."
                value={form.jalan_domisili || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    jalan_domisili: e.target.value,
                  }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.jalan_domisili?.join(",")}
              />
            </div>
          </SectionCard>

          {/* Family's Data */}
          <SectionCard title="Family's Data">
            <div>
              <Label required>Relationship</Label>
              <Select
                value={form.kontak_darurat_hubungan || ""}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Relationship" />
                </SelectTrigger>
                <SelectContent>
                  {listHubunganKeluarga?.map(
                    (option: Option, index: number) => (
                      <SelectItem key={index} value={option.value}>
                        {option.labelEn}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_hubungan?.join(
                  ",",
                )}
              />
            </div>

            <div>
              <Label required>Contact Name</Label>
              <Input
                required
                placeholder="Enter Contact Name..."
                value={form.kontak_darurat_nama || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    kontak_darurat_nama: e.target.value,
                  }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_nama?.join(
                  ",",
                )}
              />
            </div>

            <div className="lg:col-span-2">
              <Label>Emergency Contact Phone Number</Label>
              <PhoneInputWithCountrySelect
                placeholder="Enter phone number"
                value={form.kontak_darurat_hp || ""}
                onChange={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    kontak_darurat_hp: newValue || "",
                  }))
                }
                className="bg-secondary rounded-full py-2 px-3 border border-input"
              />
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_hp?.join(
                  ",",
                )}
              />
            </div>
          </SectionCard>

          {/* ACADEMIC BACKGROUND */}

          <SectionCard title="Academic Background">
            <div className="lg:col-span-2">
              <Label required>University of Origin</Label>
              <Combobox
                serverSide
                open={openAsalUniversitas}
                setOpen={setOpenAsalUniversitas}
                value={form.asal_pt || ""}
                setValue={(val) =>
                  setForm((prev) => ({ ...prev, asal_pt: val }))
                }
                onChange={(e) => setSearchQueryUniversitas(e.target.value)}
                options={optionsPt}
                placeholder="Search University..."
                isPending={isPendingOptionsPt}
                searchQueryValue={searchQueryUniversitas}
              />
              <InputError
                text={error?.response?.data?.errors?.asal_pt?.join(",")}
              />
            </div>

            {isNApt ? (
              <>
                <div className="lg:col-span-2">
                  <Label required>Name of Origin University</Label>
                  <Input
                    placeholder="Type it here"
                    value={form.asal_pt_na || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        asal_pt_na: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.asal_pt?.join(",")}
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label required>
                    Major / Study Program at Origin University
                  </Label>
                  <Input
                    placeholder="Type it here"
                    value={form.asal_prodi_na || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        asal_prodi_na: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            ) : (
              <>
                {optionsAsalProdi.length == 0 ? (
                  <div className="lg:col-span-2">
                    <Label required>
                      {" "}
                      Major / Study Program at Origin University
                    </Label>
                    <Input
                      placeholder="Type it here."
                      value={form.asal_prodi_na || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          asal_prodi_na: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <div className="lg:col-span-2">
                    <Label required>Major / Study Program</Label>
                    <Combobox
                      open={openAsalProdi}
                      setOpen={setOpenAsalProdi}
                      value={form.asal_prodi || ""}
                      setValue={(val) =>
                        setForm((prev) => ({ ...prev, asal_prodi: val }))
                      }
                      options={optionsAsalProdi}
                      placeholder="Select Study Program..."
                      isPending={isPendingOptionsPt}
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_prodi?.join(
                        ",",
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </SectionCard>
          {/* EMPLOYMENT */}
          {/* <SectionCard title="Employment Status">
            <div className="lg:col-span-2">
              <Label required>Are you currently employed?</Label>
              <div className="mt-3 flex gap-6">
                <div
                  className={`flex items-center gap-3 border p-4 rounded-xl cursor-pointer transition-colors w-full ${
                    form.is_bekerja === false
                      ? "bg-secondary border-primary"
                      : "hover:bg-accent"
                  }`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, is_bekerja: false }))
                  }
                >
                  <Checkbox checked={form.is_bekerja === false} />
                  <Label className="cursor-pointer">Unemployed</Label>
                </div>

                <div
                  className={`flex items-center gap-3 border p-4 rounded-xl cursor-pointer transition-colors w-full ${
                    form.is_bekerja === true
                      ? "bg-secondary border-primary"
                      : "hover:bg-accent"
                  }`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, is_bekerja: true }))
                  }
                >
                  <Checkbox checked={form.is_bekerja === true} />
                  <Label className="cursor-pointer">Employed</Label>
                </div>
              </div>
              <InputError
                text={error?.response?.data?.errors?.is_bekerja?.join(",")}
              />
            </div>

            {form.is_bekerja && (
              <>
                <div className="lg:col-span-2 border-t pt-5 mt-2">
                  <div className="grid lg:grid-cols-2 gap-4">
                    <div>
                      <Label required>Employer / Institution Name</Label>
                      <Input
                        required
                        value={form.tempat_bekerja || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            tempat_bekerja: e.target.value,
                          }))
                        }
                        placeholder="Enter Institution Name..."
                      />
                      <InputError
                        text={error?.response?.data?.errors?.tempat_bekerja?.join(
                          ",",
                        )}
                      />
                    </div>
                    <div>
                      <Label required>Monthly Gross Income</Label>
                      <Input
                        required
                        type="number"
                        value={form.penghasilan_bekerja || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            penghasilan_bekerja: e.target.value,
                          }))
                        }
                        placeholder="Enter amount..."
                      />
                      <InputError
                        text={error?.response?.data?.errors?.penghasilan_bekerja?.join(
                          ",",
                        )}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </SectionCard> */}
        </>
      )}
    </div>
  );
}
